import React, { useState, useEffect, useCallback } from 'react';
import { auth, firestore, storage } from '../../firebase/config';
import { doc, getDoc, setDoc, updateDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BusinessAddressPicker from './BusinessAddressPicker';

const BusinessProfile = () => {
  const [user, setUser] = useState(null);
  const [businessProfile, setBusinessProfile] = useState({
    name: '',
    type: '',
    about: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipcode: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    },
    contactInfo: {
      email: '',
      phone: ''
    },
    employeeScale: '',
    yearFounded: new Date().getFullYear(),
    taxId: ''
  });
  const [businessImage, setBusinessImage] = useState(null);
  const [businessImageUrl, setBusinessImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Memoized address change handler to prevent unnecessary re-renders
  const handleAddressChange = useCallback((addressData) => {
    setBusinessProfile(prev => ({
      ...prev,
      address: addressData
    }));
  }, []);

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      setUser(currentUser);

      // Get user data
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Get business profile if exists
        if (userData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', userData.businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            setBusinessProfile(businessData);
            if (businessData.businessImage?.path) {
              // Get image URL from storage
              try {
                const imageRef = ref(storage, businessData.businessImage.path);
                const imageUrl = await getDownloadURL(imageRef);
                setBusinessImageUrl(imageUrl);
              } catch (error) {
                console.log('No image found');
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setBusinessImage(e.target.files[0]);
      setBusinessImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('No current user found');
        // alert('Please log in to save your profile.'); // Disabled notification
        setSaving(false);
        return;
      }

      console.log('Saving business profile for user:', currentUser.uid);

      // Get user data to get business ID
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        console.error('User document not found');
        // alert('User profile not found. Please try logging out and back in.'); // Disabled notification
        setSaving(false);
        return;
      }

      const userData = userDoc.data();
      let businessId = userData.businessId;

      console.log('Current business ID:', businessId);

      // If no business exists, create one
      if (!businessId) {
        console.log('Creating new business document');
        const businessDocRef = doc(collection(firestore, 'businesses'));
        businessId = businessDocRef.id;
        
        console.log('Generated business ID:', businessId);
        
        // Update user with business ID
        await updateDoc(doc(firestore, 'users', currentUser.uid), {
          businessId: businessId,
          isBusinessProfileComplete: true
        });
        
        console.log('Updated user with business ID');
      }

      // Upload image if provided
      let businessImagePath = businessProfile.businessImage?.path || '';
      if (businessImage) {
        console.log('Uploading business image');
        const fileExtension = businessImage.name.split('.').pop();
        const imageFileName = `${currentUser.uid}_${businessId}_logo.${fileExtension}`;
        businessImagePath = `bussinessImage/${imageFileName}`;
        
        const imageRef = ref(storage, businessImagePath);
        await uploadBytes(imageRef, businessImage);
        console.log('Business image uploaded');
      }

      // Save business profile
      const businessData = {
        ...businessProfile,
        id: businessId,
        managerUserId: currentUser.uid,
        businessImage: {
          path: businessImagePath,
          type: 'storageReference'
        },
        updatedAt: new Date().toISOString()
      };

      console.log('Saving business data:', businessData);

      // Add createdAt for new businesses
      const existingBusiness = await getDoc(doc(firestore, 'businesses', businessId));
      if (!existingBusiness.exists()) {
        businessData.createdAt = new Date().toISOString();
        console.log('Added createdAt for new business');
      }

      await setDoc(doc(firestore, 'businesses', businessId), businessData);
      console.log('Business profile saved successfully');

      // Update user profile completion status
      await updateDoc(doc(firestore, 'users', currentUser.uid), {
        isBusinessProfileComplete: true
      });

              // alert('Business profile saved successfully!'); // Disabled notification
    } catch (error) {
      console.error('Error saving business profile:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
              // alert('Error saving profile. Please try again.'); // Disabled notification
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Business Profile</h1>
        <p className="text-gray-600">Update your business information and settings.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Business Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Logo
            </label>
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden border">
                {businessImageUrl ? (
                  <img src={businessImageUrl} alt="Business Logo" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <label htmlFor="business-logo" className="cursor-pointer text-orange-600 hover:text-orange-500 text-sm">
                Upload Logo
                <input
                  id="business-logo"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Business Name */}
          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
              Business Name *
            </label>
            <input
              id="businessName"
              type="text"
              required
              value={businessProfile.name}
              onChange={(e) => setBusinessProfile({...businessProfile, name: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Business Type */}
          <div>
            <label htmlFor="businessType" className="block text-sm font-medium text-gray-700">
              Business Type *
            </label>
            <select
              id="businessType"
              required
              value={businessProfile.type}
              onChange={(e) => setBusinessProfile({...businessProfile, type: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select Business Type</option>
              <option value="Restaurant">Restaurant</option>
              <option value="Cafe">Cafe</option>
              <option value="Bar">Bar</option>
              <option value="Hotel">Hotel</option>
              <option value="Catering">Catering</option>
              <option value="Restaurant Group">Restaurant Group</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* About Business */}
          <div>
            <label htmlFor="about" className="block text-sm font-medium text-gray-700">
              About Business
            </label>
            <textarea
              id="about"
              rows={4}
              value={businessProfile.about}
              onChange={(e) => setBusinessProfile({...businessProfile, about: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              placeholder="Brief description of your business..."
            />
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Business Address</h4>
            <BusinessAddressPicker
              address={businessProfile.address}
              onAddressChange={handleAddressChange}
            />
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Contact Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="email"
                placeholder="Business Email"
                value={businessProfile.contactInfo.email}
                onChange={(e) => setBusinessProfile({
                  ...businessProfile,
                  contactInfo: {...businessProfile.contactInfo, email: e.target.value}
                })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              />
              <input
                type="tel"
                placeholder="Business Phone"
                value={businessProfile.contactInfo.phone}
                onChange={(e) => setBusinessProfile({
                  ...businessProfile,
                  contactInfo: {...businessProfile.contactInfo, phone: e.target.value}
                })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="employeeScale" className="block text-sm font-medium text-gray-700">
                Employee Scale
              </label>
              <select
                id="employeeScale"
                value={businessProfile.employeeScale}
                onChange={(e) => setBusinessProfile({...businessProfile, employeeScale: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Select Size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="200+">200+ employees</option>
              </select>
            </div>
            <div>
              <label htmlFor="yearFounded" className="block text-sm font-medium text-gray-700">
                Year Founded
              </label>
              <input
                id="yearFounded"
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={businessProfile.yearFounded}
                onChange={(e) => setBusinessProfile({...businessProfile, yearFounded: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          {/* Tax ID */}
          <div>
            <label htmlFor="taxId" className="block text-sm font-medium text-gray-700">
              Tax ID (Optional)
            </label>
            <input
              id="taxId"
              type="text"
              value={businessProfile.taxId}
              onChange={(e) => setBusinessProfile({...businessProfile, taxId: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile; 